import { T } from '@tolgee/react';
import { Drawer } from 'antd';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { CheckSvg } from '../../assets/icons/CheckSvg';
import theme from '../../assets/theme';
import HybridForm from '../../components/_organisms/HybridForm/HybridForm';
import { NextPageWithLayout } from '../../components/layouts';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';

const SchoolPlanHelpPage: NextPageWithLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <LandingPageLayout
      title="School Plan Help"
      subTitle="Get Curipod for your school"
      primaryActionText="School Plan Help"
      hideBlobs={false}
      disableHeadline={true}
    >
      <PageContainer>
        <DrawerTabWrapper>
          <DrawerTab onClick={() => setDrawerOpen(true)} aria-label="See how it works">
            <PlayIcon />
            <T keyName="SchoolPlanHelp.Drawer.Open" defaultValue="How it works" />
          </DrawerTab>
        </DrawerTabWrapper>

        <Hero>
          <Title>
            <T
              keyName="SchoolPlanHelp.Title"
              defaultValue="Bring Curipod to Your Classroom"
            />
          </Title>
          <Subtitle>
            <T
              keyName="SchoolPlanHelp.Subtitle"
              defaultValue="We'll help you make the case so you and every teacher at your school can use Curipod without limits. Takes 2 minutes, and we'll be in touch within one business day."
            />
          </Subtitle>
        </Hero>

        <PerksStrip>
          <StripPerk>
            <PerkIcon>
              <CheckSvg />
            </PerkIcon>
            <T
              keyName="SchoolPlanHelp.Perks.1"
              defaultValue="Unlimited access for every teacher"
            />
          </StripPerk>
          <StripPerk>
            <PerkIcon>
              <CheckSvg />
            </PerkIcon>
            <T
              keyName="SchoolPlanHelp.Perks.2"
              defaultValue="Full access to test prep lessons"
            />
          </StripPerk>
          <StripPerk>
            <PerkIcon>
              <CheckSvg />
            </PerkIcon>
            <T
              keyName="SchoolPlanHelp.Perks.3"
              defaultValue="Follow-up within one business day"
            />
          </StripPerk>
        </PerksStrip>

        <MainContent>
          <FormSection>
            <HybridForm
              showTypeformEnding={true}
              typeformUrl="https://9uq31r57i3q.typeform.com/to/cNErYQUN"
              thankYouMessage="Thank you! We'll be in touch soon to help get Curipod set up for your school."
              metricEventNamePrefix="SchoolPlanForm"
            />
          </FormSection>
        </MainContent>

        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          destroyOnClose
          width="min(480px, 100vw)"
          closable
          title={null}
          bodyStyle={{ padding: 24, background: theme.colors.background }}
          headerStyle={{ display: 'none' }}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerHeading>
                <T
                  keyName="SchoolPlanHelp.Drawer.Heading"
                  defaultValue="How this works"
                />
              </DrawerHeading>
              <DrawerClose onClick={() => setDrawerOpen(false)} aria-label="Close">
                <CloseIcon />
              </DrawerClose>
            </DrawerHeader>
            <VideoColumn>
              <VideoFrame>
                <iframe
                  src="https://www.youtube.com/embed/s3Phh_OA-ro"
                  title="Curipod School Plan"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoFrame>
              <VideoCaption>
                <T
                  keyName="SchoolPlanHelp.Video.Caption"
                  defaultValue="See how the Curipod team helps you get the School Plan in under 2 minutes."
                />
              </VideoCaption>
            </VideoColumn>

            <InfoCard>
              <InfoCopy>
                <T
                  keyName="SchoolPlanHelp.Info.Paragraph1"
                  defaultValue="Complete this form to get support from the Curipod team in getting the School Plan at your campus. The School Plan includes unlimited access to Curipod for all teachers at your school AND full access to test prep lessons."
                />
              </InfoCopy>
              <InfoCopy>
                <T
                  keyName="SchoolPlanHelp.Info.Paragraph2"
                  defaultValue="We know getting your school to purchase a paid license isn't always easy. That's why we have a team ready to help you make the case internally. 8/10 times, we find a way to make it work."
                />
              </InfoCopy>
              <InfoCopy>
                <T
                  keyName="SchoolPlanHelp.Info.Paragraph3"
                  defaultValue="Submit the form and we'll follow up within one business day with a clear plan for moving forward."
                />
              </InfoCopy>
            </InfoCard>
          </DrawerContent>
        </Drawer>
      </PageContainer>
    </LandingPageLayout>
  );
};

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 20px 80px;
  background: ${theme.colors.background};

  @media (max-width: 992px) {
    padding: 20px 16px 60px;
  }
`;

const pulseTab = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.14);
  }
  100% {
    transform: scale(1);
  }
`;

const DrawerTabWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 60%;
  transform: translateY(-50%);
  z-index: 100;

  @media (max-width: 480px) {
    top: 70%;
  }
`;

const DrawerTab = styled.button`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  padding: 14px 18px 14px 14px;
  border: none;
  border-radius: 0 12px 12px 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  transform-origin: 0% 50%;
  animation: ${pulseTab} 0.8s ease 3s 2;

  &:hover {
    background: ${theme.colors.orangeDark};
  }

  @media (max-width: 480px) {
    padding: 12px 14px 12px 12px;
    font-size: 13px;
  }
`;

const PlayIcon = styled.span`
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${theme.colors.white};
  position: relative;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-40%, -50%);
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid ${theme.colors.orange};
  }
`;

const Hero = styled.section`
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
  padding: 0 16px;

  @media (max-width: 992px) {
    margin-bottom: 12px;
  }
`;

const MainContent = styled.section`
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 36px;
  line-height: 1.15;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 8px 0;
  max-width: 760px;

  @media (max-width: 992px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 1.4;
  font-weight: 450;
  color: ${theme.colors.text.dark};
  margin: 0;
  max-width: 640px;

  @media (max-width: 992px) {
    font-size: 15px;
  }
`;

const PerksStrip = styled.ul`
  width: 100%;
  max-width: 760px;
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 20px;
`;

const StripPerk = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.black};
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DrawerClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  color: ${theme.colors.text.dark};
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;

  &:hover {
    background: ${theme.colors.beigeDark};
  }
`;

const CloseIcon = styled.span`
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    background: currentColor;
    border-radius: 1px;
  }

  &::before {
    transform: translateY(-50%) rotate(45deg);
  }

  &::after {
    transform: translateY(-50%) rotate(-45deg);
  }
`;

const DrawerHeading = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0;
`;

const VideoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.15);
  background: ${theme.colors.black};

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoCaption = styled.p`
  font-size: 13px;
  line-height: 1.5;
  font-weight: 450;
  color: ${theme.colors.text.dark};
  margin: 0;
`;

const InfoCard = styled.div`
  background: ${theme.colors.paleBeige};
  border-radius: 10px;
  padding: 20px;
  border-left: 4px solid ${theme.colors.orange};
  display: flex;
  flex-direction: column;
`;

const InfoCopy = styled.p`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 450;
  color: ${theme.colors.text.dark};
  margin: 0 0 10px 0;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const PerkIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  flex-shrink: 0;
`;

const FormSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default SchoolPlanHelpPage;
